import { useState, useRef, useEffect } from "react";
import { Search, MapPin, Calendar, User } from "lucide-react";
import type { SightingLocation } from "@/hooks/useSightingLocations";

interface SightingsSidebarProps {
  locations: SightingLocation[];
  highlightedId: string | null;
  onItemHover: (id: string | null) => void;
  onItemClick: (id: string) => void;
}

const SightingsSidebar = ({ locations, highlightedId, onItemHover, onItemClick }: SightingsSidebarProps) => {
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const filtered = locations.filter((loc) => {
    const q = search.toLowerCase();
    return (
      loc.label.toLowerCase().includes(q) ||
      loc.year.toLowerCase().includes(q) ||
      loc.description.toLowerCase().includes(q) ||
      (loc.witness && loc.witness.toLowerCase().includes(q))
    );
  });

  useEffect(() => {
    if (highlightedId) {
      const el = itemRefs.current.get(highlightedId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [highlightedId]);

  return (
    <div className="flex flex-col h-full">
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search sightings…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <p className="text-muted-foreground text-xs mb-3">
        {filtered.length} sighting{filtered.length !== 1 ? "s" : ""} found
      </p>

      <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-thin">
        {filtered.map((loc) => (
          <div
            key={loc.id}
            ref={(el) => { if (el) itemRefs.current.set(loc.id, el); }}
            className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
              highlightedId === loc.id
                ? "bg-accent/10 border-accent shadow-md shadow-accent/10"
                : "bg-card border-border hover:border-muted-foreground/30"
            }`}
            onMouseEnter={() => onItemHover(loc.id)}
            onMouseLeave={() => onItemHover(null)}
            onClick={() => onItemClick(loc.id)}
          >
            <h3 className={`font-medium text-sm mb-1 ${
              highlightedId === loc.id ? "text-accent" : "text-foreground"
            }`}>
              {loc.label}
            </h3>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {loc.year}
              </span>
              {loc.witness && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {loc.witness}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
              {loc.description}
            </p>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No sightings match your search</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SightingsSidebar;
